import { Editor } from '@tiptap/core';
import { ParseOptions } from '@tiptap/pm/model';
import { Selection } from '@tiptap/pm/state';
import { CAPTION_RESET_GATE_META } from '../tiptap/nodes/caption/plugins/getAutoIndexCaptionsPlugin';
import { markSystemTr } from '../tiptap/plugins/shared';
import { DOCUMENT_CONTENT_REPLACED_META } from '../tiptap/plugins/ExplicitContentSelection';

const NODE_VIEW_PORTAL_TIMEOUT = 1000;
const REACT_RENDERER_SELECTOR = '.react-renderer';
const TEMPORARY_CONTENT_DOM_ATTRIBUTE = 'data-node-view-content-react';
const PORTAL_POLL_INTERVAL = 16;

const areReactNodeViewPortalsMounted = (container: HTMLElement): boolean =>
  Array.from(container.querySelectorAll<HTMLElement>(REACT_RENDERER_SELECTOR)).every((renderer) => {
    const firstElement = renderer.firstElementChild;
    return !!firstElement && !firstElement.hasAttribute(TEMPORARY_CONTENT_DOM_ATTRIBUTE);
  });

/**
 * React NodeViews are mounted through queued portals. Tiptap synchronously
 * inserts a `.react-renderer` root, but before React commits a leaf root is
 * empty and a non-leaf root contains only the temporary contentDOM. Wait until
 * every root contains its actual NodeViewWrapper before layout-dependent work.
 */
export const waitForReactNodeViewPortals = (
  editor: Editor,
  timeout = NODE_VIEW_PORTAL_TIMEOUT,
  signal?: AbortSignal
): Promise<void> => {
  const container = editor.view.dom;

  return new Promise<void>((resolve) => {
    let finished = false;
    let pollId: number | undefined;

    const observer = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => check());

    const finish = () => {
      if (finished) return;
      finished = true;
      observer?.disconnect();
      if (pollId !== undefined) window.clearTimeout(pollId);
      window.clearTimeout(timeoutId);
      signal?.removeEventListener('abort', finish);
      resolve();
    };

    const check = () => {
      if (areReactNodeViewPortalsMounted(container)) {
        finish();
        return;
      }
      if (pollId === undefined) {
        pollId = window.setTimeout(() => {
          pollId = undefined;
          check();
        }, PORTAL_POLL_INTERVAL);
      }
    };

    observer?.observe(container, { childList: true, subtree: true });
    const timeoutId = window.setTimeout(finish, timeout);
    signal?.addEventListener('abort', finish, { once: true });
    if (signal?.aborted) return finish();
    queueMicrotask(check);
  });
};

/**
 * Wait for all images in the container to load (with a timeout per image).
 *
 * @param {HTMLElement} container - The DOM container in which to look for images.
 * @param {number} timeout - Timeout in milliseconds per image (default: 2000).
 * @returns {Promise<void>}
 */
const waitForImagesInContainer = (container: HTMLElement, timeout = 2000, signal?: AbortSignal): Promise<void> => {
  const images = container.querySelectorAll('img');
  if (!images.length) return Promise.resolve();
  const promises = Array.from(images).map((img: HTMLImageElement) => {
    if (img.complete || signal?.aborted) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(timer);
        img.removeEventListener('load', finish);
        img.removeEventListener('error', finish);
        signal?.removeEventListener('abort', finish);
        resolve();
      };
      const timer = setTimeout(finish, timeout);
      img.addEventListener('load', finish, { once: true });
      img.addEventListener('error', finish, { once: true });
      signal?.addEventListener('abort', finish, { once: true });
    });
  });
  return Promise.all(promises).then(() => {});
};

/**
 * The detail payload for your custom 'editorContentSet' event.
 */
interface EditorContentSetDetail {
  content: string;
  editor: Editor;
}

/**
 * Custom event type for the 'editorContentSet' event.
 */
type TEditorContentSetEvent = CustomEvent<EditorContentSetDetail>;

/**
 * Augment the global 'WindowEventMap' so that
 * window.addEventListener("editorContentSet", ...) is typed correctly.
 */
declare global {
  interface WindowEventMap {
    editorContentSet: TEditorContentSetEvent;
  }
}

export const notifyEditorContentSet = (editor: Editor, content: string) => {
  const event: TEditorContentSetEvent = new CustomEvent('editorContentSet', {
    detail: { content, editor },
  });
  window.dispatchEvent(event);
};

/**
 * Sets the editor's content and dispatches a custom event when the content is fully updated.
 *
 * This helper sets the content via tiptap's setContent and then waits for all images in the
 * editor's content to load (using editor.view.dom) before dispatching the "editorContentSet"
 * event. This ensures that any subsequent DOM calculations are based on a fully rendered layout.
 *
 * Why default to { preserveWhitespace: 'full' }? – https://github.com/ueberdosis/tiptap/issues/873#issuecomment-1608497238
 *
 * @param {Editor} editor - The tiptap Editor instance.
 * @param {string} content - The content to set in the editor.
 * @param {ParseOptions} options - Options to pass to setContent (default: { preserveWhitespace: 'full' }).
 */

import { BubbleMenuPlugin } from '@tiptap/extension-bubble-menu';

const setContentAndNotify = (
  editor: Editor,
  content: string,
  callback?: () => void,
  options: ParseOptions = { preserveWhitespace: 'full' },
  /** `applied` says whether the content replaced the document - a `filterTransaction` can block it. */
  onPhase?: (phase: 'deferred' | 'parse' | 'portals' | 'images', ms: number, applied?: boolean) => void,
  /** A whole document puts its caret where editing starts, not where the old selection maps to, e.g. onto a last image. */
  caretAtStart = false
): (() => void) => {
  const calledAt = performance.now();
  const abortController = new AbortController();
  const unavailable = () => abortController.signal.aborted || editor.isDestroyed;
  /**
   * Defer DOM mutation after React render phase.
   * Why setTimeout? – https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1546854730
   */
  const deferred = window.setTimeout(() => {
    if (unavailable()) return;
    // Caption labels are volatile (not persisted in HTML), so same-count
    // content swaps need an explicit gate reset to trigger renumbering.
    // Literature-citation indices are persisted as `data-index` and picked up
    // by the presence gate on the next mutation - no reset required.
    // How long the deferred task waited to run. On a busy main thread this is far bigger than the parse.
    onPhase?.('deferred', performance.now() - calledAt);
    const startedParse = performance.now();
    let applied = false;
    try {
      editor.view.dispatch(editor.state.tr.setMeta(CAPTION_RESET_GATE_META, true));
      // Taken after the caption reset above, which can itself change the doc, and before the content
      // is set - so a different doc afterwards means this content, and nothing else, replaced it.
      const before = editor.state.doc;
      // Template locking rejects any transaction that does not land inside a field, and replacing the
      // whole content is one - but it is never user input, so it goes in as a system transaction.
      editor
        .chain()
        .command(({ tr }) => {
          markSystemTr(tr, { keepInHistory: true });
          tr.setMeta(DOCUMENT_CONTENT_REPLACED_META, true);
          return true;
        })
        .setContent(content, { emitUpdate: true, parseOptions: options })
        .command(({ tr }) => {
          if (caretAtStart) tr.setSelection(Selection.atStart(tr.doc));
          return true;
        })
        .run();
      applied = editor.state.doc !== before;
      editor.view.updateState(editor.state);
    } catch (error) {
      if (!unavailable()) console.error('Setting editor content failed!', error);
      return;
    }
    onPhase?.('parse', performance.now() - startedParse, applied);
    let stageStarted = performance.now();

    // React NodeView portals are queued after the ProseMirror transaction. Let
    // them mount before discovering images or announcing layout readiness.
    waitForReactNodeViewPortals(editor, NODE_VIEW_PORTAL_TIMEOUT, abortController.signal)
      .then(() => {
        if (unavailable()) return;
        onPhase?.('portals', performance.now() - stageStarted);
        stageStarted = performance.now();
        return waitForImagesInContainer(editor.view.dom, 2000, abortController.signal);
      })
      .then(() => {
        if (unavailable()) return;
        onPhase?.('images', performance.now() - stageStarted);
        // fixes bubble menu visibility issue when switching versions in read-only (InlineBlockNodeBubbleMenu.tsx):
        try {
          const elem = document.getElementById('dummy-bubble-menu')?.cloneNode(true); // we need to clone it, as BubbleMenuPlugin will remove it from the DOM

          if (elem) {
            const bubbleMenu = BubbleMenuPlugin({
              pluginKey: 'dummyBubbleMenu',
              editor,
              element: elem as HTMLElement,
              options: { placement: 'top' },
            });
            editor.registerPlugin(bubbleMenu);
          }
          editor.unregisterPlugin('dummyBubbleMenu');
        } catch (e) {
          console.error('Reloading bubble menus failed!', e);
        }

        notifyEditorContentSet(editor, content);
      })
      .catch((error) => {
        if (unavailable()) return;
        console.error('Error while waiting for images:', error);
        // In case of error, dispatch the event regardless.
        notifyEditorContentSet(editor, content);
      })
      .finally(() => {
        // Invoke the callback if provided.
        if (!unavailable() && callback) callback();
      });
    /**
     * Slight extra safety margin, allows slower renders to complete. Most people use 0 in this case though.
     * However, we want to consider the possibility of slower devices or browsers.
     */
  }, 10);

  return () => {
    window.clearTimeout(deferred);
    abortController.abort();
  };
};

export default setContentAndNotify;
